import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const StartersReadingPart7 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [story, setStory] = useState('');
  const [isHydrated, setIsHydrated] = useState(false);
  
  useEffect(() => {
    if (attemptId && exerciseId && exerciseData && exerciseData.questions && exerciseData.questions.length > 0) {
      const question = exerciseData.questions[0];
      
      // If in review mode, prioritize the answer from the API data
      if (isReviewMode && question.user_answer && typeof question.user_answer.answer === 'string') {
        setStory(question.user_answer.answer);
        setIsHydrated(true);
        return;
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedAnswer = allAnswers[exerciseId]?.[question.id];
          if (savedAnswer && typeof savedAnswer.answer === 'string') {
            setStory(savedAnswer.answer);
          }
        } catch (e) {
          console.error("Failed to parse saved answers", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, exerciseData, isReviewMode]);

  const saveAnswersToLocalStorage = useCallback((currentStory) => {
    // ... existing save logic ...
    if (!isHydrated || !attemptId || !exerciseId || !exerciseData || !exerciseData.questions || exerciseData.questions.length === 0) return;
    const question = exerciseData.questions[0];
    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answerToSave = { [question.id]: { answer: currentStory } };

    allAnswers[exerciseId] = answerToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, exerciseData]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const question = exerciseData.questions[0];
      if (question && question.accepted_answers && question.accepted_answers.length > 0) {
        const newStory = question.accepted_answers[0];
        setStory(newStory);
        saveAnswersToLocalStorage(newStory);
      }
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(story);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(story);
    };
  }, [story, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions || exerciseData.questions.length === 0) {
    return <div>Loading exercise data...</div>;
  }

  const { 
    story_image
  } = exerciseData.content_data;

  const question = exerciseData.questions[0];
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';
  
  // Fix: is_correct is at top level
  const isCorrect = question.is_correct === true || question.is_correct === 'true';

  const handleStoryChange = (event) => {
    setStory(event.target.value);
  };

  return (
    <div className="w-full h-full p-4 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-4xl mx-auto w-full py-4">
        <div className="exercise-card p-6 md:p-10 rounded-[2.5rem] bg-white dark:bg-slate-800/40 shadow-soft border border-slate-100 dark:border-slate-800">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                {story_image && story_image.map((imgUrl, index) => (
                    <div key={index} className="bg-white dark:bg-card-dark p-2 rounded-2xl shadow-sm border-2 border-slate-50 dark:border-slate-700">
                        <img
                            src={`${ASSET_BASE_URL}${imgUrl}`}
                            alt={`Story scene ${index + 1}`}
                            className="rounded-xl w-full h-auto object-cover"
                        />
                    </div>
                ))}
            </div>

            <div className="mt-8 border-t border-slate-100 dark:border-slate-700 pt-10">
                <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-hupath-scout/10 flex items-center justify-center text-hupath-scout">
                        <span className="material-symbols-outlined">edit_note</span>
                    </div>
                    <h3 className="font-display font-black text-xl text-slate-800 dark:text-white text-center">Your Story</h3>
                </div>
                <textarea
                    value={showHint ? question.accepted_answers[0] : story}
                    onChange={handleStoryChange}
                    rows="10"
                    placeholder="Write your story here... (20 or more words)"
                    className={`w-full p-8 rounded-[2rem] border-2 bg-slate-50/30 dark:bg-slate-900/50 border-blue-50 dark:border-slate-600 focus:ring-8 focus:ring-blue-50/50 focus:border-primary-blue transition-all font-body text-xl leading-relaxed text-slate-700 dark:text-slate-200 ${showHint ? 'hint-input' : ''}`}
                    disabled={isReviewMode || !isHydrated}
                />
                
                {isReviewMode && question.user_answer && typeof question.user_answer === 'object' && question.user_answer.feedback && (
                    <div className="mt-8 p-6 rounded-2xl bg-amber-50/50 dark:bg-amber-900/10 border-2 border-amber-100 dark:border-amber-800/50 animate-fade-in relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400" />
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white shadow-md shadow-amber-200 dark:shadow-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
                            </div>
                            <div>
                                <h4 className="font-display font-bold text-slate-800 dark:text-white leading-tight">Examiner's Feedback</h4>
                                <p className="text-xs text-amber-700 dark:text-amber-400 font-medium uppercase tracking-wider">Teacher Assessment</p>
                            </div>
                            
                            <div className="ml-auto flex flex-col items-end">
                                <span className="text-[10px] uppercase font-bold text-slate-400 mb-1">Score Awarded</span>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-3xl font-display font-black text-amber-600 dark:text-amber-400">
                                        {question.user_answer.gemini_score}
                                    </span>
                                    <span className="text-sm font-bold text-slate-400">/ 5</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="relative p-4 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-amber-100 dark:border-amber-900/50 shadow-sm">
                                <p className="font-body text-slate-700 dark:text-slate-200 leading-relaxed text-lg italic">
                                    "{question.user_answer.feedback}"
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <div className="px-3 py-1.5 rounded-full bg-amber-100/50 dark:bg-amber-900/30 border border-gray-200 dark:border-amber-800 flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    <span className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-tight">
                                        {question.user_answer.completeness}
                                    </span>
                                </div>
                                <div className="px-3 py-1.5 rounded-full bg-amber-100/50 dark:bg-amber-900/30 border border-gray-200 dark:border-amber-800 flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    <span className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-tight">
                                        {question.user_answer.progression}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showHint && (
                    <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <h4 className="font-display font-bold text-yellow-800 dark:text-yellow-200 mb-2">Suggested points to include:</h4>
                        <ul className="list-disc list-inside font-body text-yellow-700 dark:text-yellow-100">
                            {question.accepted_answers && question.accepted_answers.map((hint, index) => (
                                <li key={index}>{hint}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default StartersReadingPart7;